import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { useCallback, useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { faPaperPlane, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";

import Checkbox from "components/Checkbox";
import DiscussionEditor from "components/DiscussionEditor";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import ConnectLinkedin from "components/SocialButtons/Linkedin";
import ConnectTwitter from "components/SocialButtons/Twitter";
import TopNavBarMobile from "components/TopNavBar/TopNavBarMobile";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { fetchApi } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import htmlToText from "utils/htmlToText";

import styles from "./style.module.scss";

const Modal = dynamic(() => import("react-modal"));

type DiscussionComposeModalProps = {
  onClose?: () => void;
  onDiscussionPost?: (_newDiscussion?: any) => void;
  style?: "default" | "v2" | "v3";
  commentable?: any;
  defaultValue?: string;
};

const DiscussionComposeModal = ({
  onClose,
  onDiscussionPost,
  style = "v3",
  commentable,
  defaultValue,
}: DiscussionComposeModalProps) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { account } = useAuthentication();
  const { setNotification } = useNotification();
  const [postedComment, setPostedComment] = useState<any>(null);
  const [publishToTwitter, setPublishToTwitter] = useState(
    account?.twitterConnected
  );
  const [publishToLinkedIn, setPublishToLinkedIn] = useState(
    account?.linkedinConnected
  );
  const [tweetBody, setTweetBody] = useState("");
  const [linkedinBody, setLinkedinBody] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);

  const toggleModal = useCallback(() => {
    onDiscussionPost && onDiscussionPost(postedComment);

    if (onClose) {
      onClose();
      return;
    }

    router.back();
  }, [onClose, onDiscussionPost, postedComment, router]);

  const handleDiscussionPost = useCallback((newDiscussion: any) => {
    if (newDiscussion) {
      const hasPolling =
        !!newDiscussion?.["comment-poll"] || newDiscussion?.commentPoll;
      const hasPrediction =
        !!newDiscussion?.["comment-prediction"] ||
        newDiscussion?.commentPrediction;

      if (hasPolling || hasPrediction) {
        toggleModal();
        return;
      }

      setPostedComment(newDiscussion);
    }

    const body = (newDiscussion?.body || "").replace(
      /<a[^>]+href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
      (_: any, href: any, text: any) => {
        const fullHref =
          href.startsWith("http://") || href.startsWith("https://")
            ? href
            : `${emberAppBaseUrl}${href}`;
        const linkText = htmlToText(text).trim();
        return linkText ? `${linkText} (${fullHref})` : fullHref;
      }
    );

    const LIMIT = 200;
    const fullText = htmlToText(body) || "";
    let truncated = fullText.slice(0, LIMIT);
    if (fullText.length > LIMIT) {
      const lastOpen = truncated.lastIndexOf("(");
      if (lastOpen !== -1 && !truncated.slice(lastOpen).includes(")")) {
        truncated = truncated.slice(0, lastOpen).trimEnd();
      }
    }

    const LINKEDIN_LIMIT = 350;
    let truncatedLinkedin = fullText.slice(0, LINKEDIN_LIMIT);
    if (fullText.length > LINKEDIN_LIMIT) {
      const lastOpen = truncatedLinkedin.lastIndexOf("(");
      if (lastOpen !== -1 && !truncatedLinkedin.slice(lastOpen).includes(")")) {
        truncatedLinkedin = truncatedLinkedin.slice(0, lastOpen).trimEnd();
      }
    }

    const plainText = fullText.length > LIMIT ? truncated + "..." : fullText;
    const plainTextLinkedin =
      fullText.length > LINKEDIN_LIMIT ? truncatedLinkedin + "..." : fullText;

    setTweetBody(plainText);
    setLinkedinBody(plainTextLinkedin);
  }, []);

  const handleFinish = useCallback(async () => {
    if (!publishToTwitter && !publishToLinkedIn) return;
    if (!postedComment) return;

    const commentId = postedComment?.id;

    if (publishToTwitter || publishToLinkedIn) {
      try {
        setIsPublishing(true);
        await fetchApi(`/api/v2/comments/${commentId}`, {
          method: "PATCH",
          body: JSON.stringify({
            data: {
              type: "comments",
              id: commentId,
              attributes: {
                ...(publishToTwitter && {
                  "post-on-twitter": true,
                  "tweet-body": tweetBody,
                }),
                ...(publishToLinkedIn && {
                  "post-on-linkedin": true,
                  "linkedin-body": linkedinBody,
                }),
              },
            },
          }),
        });
        setNotification("Discussion posted on social successfully", "success");
      } finally {
        setIsPublishing(false);
      }
    }

    toggleModal();
  }, [
    linkedinBody,
    postedComment,
    publishToLinkedIn,
    publishToTwitter,
    setNotification,
    toggleModal,
    tweetBody,
  ]);

  useEffect(() => {
    if (account?.twitterConnected) {
      setPublishToTwitter(true);
    }

    if (account?.linkedinConnected) {
      setPublishToLinkedIn(true);
    }
  }, [account?.twitterConnected, account?.linkedinConnected]);

  const twitterPublishing = useMemo(() => {
    return (
      <div className="mb-[15px]">
        <Checkbox
          checked={publishToTwitter}
          onClick={() => setPublishToTwitter(!publishToTwitter)}
          className="mb-[15px]"
          disabled={!account?.twitterConnected}
        >
          <span className="text-[14px] ml-[10px] font-bold">
            Post to X (Twitter)
          </span>
        </Checkbox>
        {account?.twitterConnected ? (
          <>
            <textarea
              className={twMerge(
                "w-full min-h-[100px] p-[10px]",
                "border border-solid border-[#ddd] rounded-[10px]",
                "text-[14px] resize-y",
                "focus:outline-none focus:border-smart-teal",
                (!account?.twitterConnected || !publishToTwitter) &&
                  "!text-black/20 cursor-not-allowed"
              )}
              disabled={!account?.twitterConnected || !publishToTwitter}
              onChange={(e) => setTweetBody(e.target.value)}
            >
              {tweetBody}
            </textarea>
            <span className="text-[12px] text-inactive-gray">
              This text will accompany shared URL to your discussion.
            </span>
          </>
        ) : (
          <div className="flex items-center justify-center">
            <ConnectTwitter />
          </div>
        )}
      </div>
    );
  }, [account?.twitterConnected, publishToTwitter, tweetBody]);

  const linkedinPublishing = useMemo(() => {
    return (
      <div className="mb-[15px]">
        <Checkbox
          checked={publishToLinkedIn}
          onClick={() => setPublishToLinkedIn(!publishToLinkedIn)}
          className="mb-[15px]"
          disabled={!account?.linkedinConnected}
        >
          <span className="text-[14px] font-bold ml-[10px]">
            Post to LinkedIn
          </span>
        </Checkbox>
        {account?.linkedinConnected ? (
          <>
            <textarea
              className={twMerge(
                "w-full min-h-[100px] p-[10px]",
                "border border-solid border-[#ddd] rounded-[10px]",
                "text-[14px] resize-y",
                "focus:outline-none focus:border-smart-teal",
                (!account?.linkedinConnected || !publishToLinkedIn) &&
                  "!text-black/20 cursor-not-allowed"
              )}
              disabled={!account?.linkedinConnected || !publishToLinkedIn}
              onChange={(e) => setLinkedinBody(e.target.value)}
            >
              {linkedinBody}
            </textarea>
            <span className="text-[12px] text-inactive-gray">
              This text will accompany shared URL to your discussion.
            </span>
          </>
        ) : (
          <div className="flex items-center justify-center">
            <ConnectLinkedin />
          </div>
        )}
      </div>
    );
  }, [account?.linkedinConnected, publishToLinkedIn, linkedinBody]);

  const title = useMemo(() => {
    if (postedComment) {
      return <h4 className="mb-0 text-[20px]">Share your post</h4>;
    }

    if (commentable) {
      return (
        <h4 className="mb-0 text-[20px]">
          Post new discussion on {commentable?.name || commentable?.prettyName}
        </h4>
      );
    }

    return <h4 className="mb-0 text-[20px]">Post a discussion</h4>;
  }, [postedComment, commentable]);

  const shouldShareToSocials = useMemo(() => {
    if (!postedComment) return false;

    const hasPolling =
      !!postedComment?.["comment-poll"] || postedComment?.commentPoll;
    const hasPrediction =
      !!postedComment?.["comment-prediction"] ||
      postedComment?.commentPrediction;
    return !(hasPolling || hasPrediction);
  }, [postedComment]);

  return (
    <Modal
      portalClassName={styles.modal}
      overlayClassName={styles.overlay}
      className={classNames(
        styles.content,
        mobile ? styles.mobileLayout : styles.desktopLayout
      )}
      isOpen
      onRequestClose={toggleModal}
      onAfterOpen={() => {
        document.body.style.overflowY = "hidden";
      }}
      onAfterClose={() => {
        document.body.style.overflowY = "";
      }}
    >
      {mobile && (
        <TopNavBarMobile
          onGoBack={toggleModal}
          customBackButtonLabel="Cancel"
        />
      )}
      <div
        className={twMerge(
          "flex justify-between items-center border-0 border-b border-solid border-nearly-white",
          "mb-[10px] md:mb-0 px-[10px] md:px-[20px] py-[5px] pb-[10px] md:py-[15px]"
        )}
      >
        {title}
        <div className="hidden md:block">
          <FontAwesomeIcon
            icon={faTimes}
            className="text-[20px] text-shady-gray cursor-pointer"
            onClick={toggleModal}
          />
        </div>
      </div>
      <div className="p-[10px] pr-[15px] md:p-[20px]">
        {shouldShareToSocials ? (
          <div>
            <p className="text-[14px] text-shady-gray">
              Your discussion has been successfully posted on Smartkarma
              platform. Share it on social media to gain more visibility.
            </p>

            {twitterPublishing}
            {linkedinPublishing}

            <div className="flex justify-end mt-[20px] gap-[15px]">
              <button
                className={twMerge(
                  "flex items-center justify-center gap-[5px] cursor-pointer min-h-[40px]",
                  "rounded-full px-[15px] py-[5px] font-bold",
                  "border border-solid border-nearly-white",
                  "bg-smart-teal hover:bg-dark-teal",
                  "text-white",
                  isPublishing && "opacity-70 cursor-not-allowed",
                  !publishToLinkedIn &&
                    !publishToTwitter &&
                    "opacity-70 !cursor-not-allowed"
                )}
                onClick={handleFinish}
                disabled={
                  isPublishing || (!publishToLinkedIn && !publishToTwitter)
                }
              >
                {isPublishing ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="mr-[5px] animate-spin"
                  />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} className="mr-[5px]" />
                )}
                Post
              </button>
              <button
                className={twMerge(
                  "border border-solid border-bearish-red min-h-[40px]",
                  "bg-white hover:bg-bearish-red",
                  "text-bearish-red hover:text-white",
                  "font-bold",
                  "rounded-full  px-[15px] py-[5px]"
                )}
                onClick={toggleModal}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <DiscussionEditor
            commentable={commentable}
            afterSubmit={handleDiscussionPost}
            style={style}
            defaultValue={defaultValue}
          />
        )}
      </div>
    </Modal>
  );
};

export default DiscussionComposeModal;
